import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import client from "@/api/client";
import OnboardingOverlay from "@/components/OnboardingOverlay";
import NextActionPanel from "@/components/NextActionPanel";
import WorkspaceHeader from "@/components/WorkspaceHeader";
import { useDemoMode } from "@/hooks/useDemoMode";
import { DemoToggle, DemoBanner, DemoDataDisplay } from "@/components/DemoDataOverlay";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
         Tooltip, ResponsiveContainer, Cell, ReferenceLine } from "recharts";
import { TrendingDown, TrendingUp, Award, Building2, Target, Zap,
         ArrowRight, AlertTriangle, CheckCircle2, Leaf, ChevronRight } from "lucide-react";
import StressTest from "@/components/StressTest";
import CorporateExecutiveInsights from "@/components/CorporateExecutiveInsights";
import { SectionCard, MetricCard } from "@/components/ds";

const CAT_COLORS = {
  electricity:"#3B82F6", fuel:"#F59E0B", transport:"#8B5CF6",
  waste:"#10B981", materials:"#EC4899", process:"#EF4444", other:"#94A3B8",
};

const STATUS_COLORS = {
  planned:"bg-slate-100 text-slate-600", in_progress:"bg-blue-50 text-blue-700",
  completed:"bg-emerald-50 text-emerald-700", cancelled:"bg-red-50 text-red-500",
};

const CAT_ICONS = {
  renewable_energy:"☀️", energy_efficiency:"⚡", fleet_electrification:"🚗",
  waste_reduction:"♻️", process_optimisation:"🔧", materials_substitution:"📦", other:"💡",
};

// ── Sub-components ─────────────────────────────────────────────────────────
function KPICard({ label, value, unit, sub, delta, color, icon: Icon }) {
  return (
    <MetricCard
      label={label}
      value={value}
      unit={unit}
      sub={sub}
      delta={delta}
      color={color || "text-slate-900"}
      icon={Icon}
    />
  );
}

function TargetGauge({ target, currentTco2e }) {
  if (!target) return (
    <div className="text-center py-6">
      <Target size={24} className="mx-auto mb-2 text-slate-200" />
      <p className="text-xs text-slate-400">No corporate target set</p>
    </div>
  );
  const base = target.base_year_tco2e || 1;
  const targetTco2e = target.target_tco2e || 0;
  const reductionPct = Math.min(100, ((base - currentTco2e) / base) * 100);
  const requiredPct = target.target_reduction_pct || 100;
  const pctOfTarget = Math.min(100, (reductionPct / requiredPct) * 100);
  const onTrack = !target.gap_tco2e || target.gap_tco2e <= 0;
  return (
    <div className="space-y-3">
      <div className="flex justify-between items-start">
        <div>
          <p className="text-xs font-semibold text-slate-900">{target.name}</p>
          <p className="text-xs text-slate-400">{target.target_year} target: {targetTco2e?.toFixed(1)} tCO₂e ({target.target_reduction_pct}% reduction)</p>
        </div>
        <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${onTrack ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
          {onTrack ? "On Track" : "Off Track"}
        </span>
      </div>
      {/* Progress bar */}
      <div>
        <div className="flex justify-between text-xs mb-1.5">
          <span className="text-slate-500">Progress to target</span>
          <span className="font-bold text-slate-900">{pctOfTarget.toFixed(0)}%</span>
        </div>
        <div className="h-3 bg-slate-100 rounded-full overflow-hidden relative">
          <div className={`h-full rounded-full transition-all duration-500 ${onTrack ? "bg-[#059669]" : "bg-amber-500"}`}
               style={{ width: `${Math.max(0, pctOfTarget)}%` }} />
        </div>
      </div>
      {target.gap_tco2e !== undefined && (
        <div className={`flex items-center gap-2 p-2.5 rounded-lg ${target.gap_tco2e > 0 ? "bg-amber-50 border border-amber-200" : "bg-emerald-50 border border-emerald-200"}`}>
          {target.gap_tco2e > 0
            ? <AlertTriangle size={13} className="text-amber-600 flex-shrink-0" />
            : <CheckCircle2 size={13} className="text-emerald-600 flex-shrink-0" />}
          <p className="text-xs">
            {target.gap_tco2e > 0
              ? <><strong className="text-amber-800">{target.gap_tco2e.toFixed(2)} tCO₂e above target.</strong> Gap must close to stay on glide path.</>
              : <strong className="text-emerald-800">Ahead of target by {Math.abs(target.gap_tco2e).toFixed(2)} tCO₂e.</strong>}
          </p>
        </div>
      )}
    </div>
  );
}

function AssetHeatmap({ assets, totalTco2e }) {
  const navigate = useNavigate();
  if (!assets?.length) return <p className="text-xs text-slate-400 py-4 text-center">No assets tracked yet.</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs" data-testid="asset-heatmap">
        <thead>
          <tr className="bg-slate-50 border-b border-slate-200">
            <th className="text-left px-4 py-2.5 font-semibold text-slate-500 uppercase tracking-wider">Asset</th>
            <th className="text-left px-4 py-2.5 font-semibold text-slate-500 uppercase tracking-wider hidden sm:table-cell">Unit</th>
            <th className="text-right px-4 py-2.5 font-semibold text-slate-500 uppercase tracking-wider">tCO₂e</th>
            <th className="text-right px-4 py-2.5 font-semibold text-slate-500 uppercase tracking-wider hidden md:table-cell">Share</th>
            <th className="text-left px-4 py-2.5 font-semibold text-slate-500 uppercase tracking-wider">Score</th>
            <th className="text-left px-4 py-2.5 font-semibold text-slate-500 uppercase tracking-wider hidden lg:table-cell">Benchmark</th>
            <th className="text-left px-4 py-2.5 font-semibold text-slate-500 uppercase tracking-wider">Status</th>
            <th className="px-3 py-2.5"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-50">
          {[...assets].sort((a, b) => (b.total_tco2e || 0) - (a.total_tco2e || 0)).map(asset => {
            const pct = asset.pct_of_total;
            const hasData = asset.total_tco2e !== null;
            const benchBg = { better_than_sector: "bg-emerald-100 text-emerald-700",
              near_sector: "bg-amber-100 text-amber-700",
              worse_than_sector: "bg-red-100 text-red-700" };
            const scoreColor = asset.climate_score >= 70 ? "text-[#059669]" : asset.climate_score >= 50 ? "text-amber-600" : "text-red-500";
            return (
              <tr key={asset.asset_id}
                  className={`hover:bg-slate-50 transition-colors ${!hasData ? "opacity-50" : ""}`}
                  data-testid={`asset-row-${asset.asset_id}`}>
                <td className="px-4 py-3">
                  <p className="font-semibold text-slate-900">{asset.asset_name}</p>
                  {asset.location && <p className="text-slate-400 text-xs">{asset.location}</p>}
                </td>
                <td className="px-4 py-3 text-slate-400 hidden sm:table-cell">{asset.bu_name || "—"}</td>
                <td className="px-4 py-3 text-right">
                  {hasData ? (
                    <span className="font-bold tabular-nums text-slate-900">{asset.total_tco2e?.toFixed(2)}</span>
                  ) : <span className="text-slate-300">No data</span>}
                </td>
                <td className="px-4 py-3 hidden md:table-cell text-right">
                  {pct != null ? (
                    <div className="flex items-center justify-end gap-2">
                      <div className="w-12 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-[#059669] rounded-full" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="text-slate-500 w-8 text-right">{pct}%</span>
                    </div>
                  ) : "—"}
                </td>
                <td className="px-4 py-3">
                  {asset.climate_score != null ? (
                    <span className={`font-bold tabular-nums ${scoreColor}`}>{asset.climate_score.toFixed(0)}/100</span>
                  ) : "—"}
                </td>
                <td className="px-4 py-3 hidden lg:table-cell">
                  {asset.benchmark_position ? (
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${benchBg[asset.benchmark_position] || "bg-slate-100 text-slate-500"}`}>
                      {asset.benchmark_position === "better_than_sector" ? "Better" : asset.benchmark_position === "near_sector" ? "Near" : "Above"}
                    </span>
                  ) : "—"}
                </td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${!hasData ? "bg-slate-50 text-slate-400" : "bg-emerald-50 text-emerald-700"}`}>
                    {hasData ? (asset.status?.replace(/_/g, " ") || "measured") : "unmeasured"}
                  </span>
                </td>
                <td className="px-3 py-3">
                  {asset.assessment_id && (
                    <button onClick={() => navigate(`/assessments/${asset.assessment_id}`)}
                      className="text-slate-300 hover:text-[#059669]"><ChevronRight size={14} /></button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── Main Dashboard ─────────────────────────────────────────────────────────
export default function CorporateDashboard() {
  const navigate = useNavigate();
  const { currentOrg } = useAuth();
  const [dash, setDash] = useState(null);
  const [trend, setTrend] = useState(null);
  const [allInitiatives, setAllInitiatives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reportingYear, setReportingYear] = useState(null);
  const [showStress, setShowStress] = useState(false);

  // Derive orgId from authenticated user's org — never hardcode
  const orgId = currentOrg?.id;
  const { demoMode, toggleDemo, exitDemo } = useDemoMode("corporate");
  const load = useCallback(async () => {
    if (!orgId) return;  // Wait for org to resolve from AuthContext
    setLoading(true);
    try {
      const [dRes, tRes, iRes] = await Promise.all([
        client.get(`/corporate/orgs/${orgId}/dashboard${reportingYear ? `?reporting_year=${reportingYear}` : ""}`),
        client.get(`/corporate/orgs/${orgId}/performance`),
        client.get(`/corporate/orgs/${orgId}/initiatives`),
      ]);
      setDash(dRes.data);
      setTrend(tRes.data);
      setAllInitiatives(iRes.data || []);
    } catch {}
    setLoading(false);
  }, [orgId, reportingYear]);

  useEffect(() => { load(); }, [load]);

  if (loading) return (
    <div className="p-6 lg:p-8 space-y-4">
      <div className="h-8 w-64 bg-slate-200 rounded animate-pulse" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => <div key={i} className="h-28 bg-white border border-slate-200 rounded-xl animate-pulse" />)}
      </div>
    </div>
  );
  if (!dash) return <div className="p-8 text-center text-slate-500">No data available.</div>;

  const emissions = dash.emissions || {};
  const assets = emissions.assets || [];
  const target = dash.primary_target;
  const initiatives = dash.initiatives || {};
  const latestTrend = trend?.trend?.slice(-2) || [];
  const yoyDelta = latestTrend.length === 2 ? latestTrend[1]?.yoy_delta_pct : null;

  // Chart data
  const scopeData = [
    { name: "Scope 1\nDirect", value: parseFloat(emissions.scope_1_tco2e?.toFixed(2) || 0), fill: "#0F172A" },
    { name: "Scope 2\nIndirect", value: parseFloat(emissions.scope_2_tco2e?.toFixed(2) || 0), fill: "#059669" },
    { name: "Scope 3\nValue Chain", value: parseFloat(emissions.scope_3_tco2e?.toFixed(2) || 0), fill: "#3B82F6" },
  ];
  const catData = Object.entries(emissions.by_category || {})
    .filter(([, v]) => v > 0)
    .sort((a, b) => b[1] - a[1])
    .map(([k, v]) => ({ name: k.charAt(0).toUpperCase() + k.slice(1), value: parseFloat(v.toFixed(2)) }));

  const trendData = trend?.trend?.map(t => ({
    year: String(t.year), total: t.total_tco2e,
    target: target ? parseFloat((target.base_year_tco2e * (1 - (target.target_reduction_pct / 100) * (t.year - target.base_year) / (target.target_year - target.base_year))).toFixed(2)) : null
  })) || [];

  return (
    <div className="p-6 lg:p-8 space-y-6 animate-fade-in" data-testid="corporate-dashboard">
      {/* First-time onboarding */}
      <OnboardingOverlay workspace="corporate" />

      {/* WorkspaceHeader — consistent with other workspaces */}
      <WorkspaceHeader
        workspace="corporate"
        entityName={demoMode ? "Lafarge Africa Plc (Demo)" : dash.org?.name}
        subTitle={demoMode ? "Cement Manufacturing · Nigeria · Abuja FCT · Example Data" : `${dash.org?.sector || ""} · ${dash.org?.country || ""} · FY${dash.reporting_year}`}
        stats={[
          { label: "Reporting Year", value: String(dash.reporting_year || "—"), color: "text-indigo-400" },
          { label: "Total Emissions", value: demoMode ? "19.6kt" : (dash.emissions?.total ? `${(dash.emissions.total / 1000).toFixed(0)}kt` : "—"), color: "text-amber-400" },
          { label: "Active Initiatives", value: String(allInitiatives?.length || 0), color: "text-teal-400" },
          { label: "Status", value: dash.primary_target ? "Target Set" : "No Target", color: "text-slate-400" },
        ]}
        data-testid="workspace-header-corporate">
        <div className="flex gap-2 mt-1">
          <DemoToggle workspace="corporate" demoMode={demoMode} onToggle={toggleDemo} />
          <select
            value={reportingYear || dash.reporting_year || ""}
            onChange={e => setReportingYear(e.target.value ? parseInt(e.target.value) : null)}
            className="h-7 px-2 border border-white/20 rounded text-xs bg-white/10 text-white">
            {[2024, 2023, 2022, 2021].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </WorkspaceHeader>

      {demoMode && <DemoBanner workspace="corporate" onExit={exitDemo} />}

      {demoMode ? (
        <DemoDataDisplay workspace="corporate" />
      ) : (
        <>
      {/* Next action panel */}
      <NextActionPanel workspace="corporate" />

      {/* V1 principle */}
      <div className="flex items-center gap-2 p-2.5 bg-slate-50 border border-slate-200 rounded-lg">
        <Leaf size={12} className="text-[#059669] flex-shrink-0" />
        <p className="text-xs text-slate-500">
          Corporate module aggregates V1 baseline outputs. All emissions data from verified GHG assessments. No duplicate calculations.
        </p>
      </div>

      {/* Executive Insights Panel */}
      <CorporateExecutiveInsights
        dashData={dash}
        trendData={trend}
        initiatives={allInitiatives}
      />

      {/* KPI Strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="corporate-kpis">
        <MetricCard
          label="Total Emissions"
          value={emissions.total_tco2e?.toFixed(2) || "0"}
          unit="tCO₂e"
          sub={`${emissions.coverage?.covered || 0}/${emissions.coverage?.total || 0} assets covered`}
          delta={yoyDelta}
          icon={TrendingDown}
        />
        <MetricCard
          label="Coverage"
          value={`${emissions.coverage?.pct || 0}%`}
          sub={`${emissions.coverage?.covered || 0} of ${emissions.coverage?.total || 0} assets measured`}
          color={emissions.coverage?.pct === 100 ? "text-[#059669]" : "text-amber-600"}
          icon={Building2}
        />
        <MetricCard
          label="Gap to Target"
          value={target?.gap_tco2e != null ? `${Math.abs(target.gap_tco2e).toFixed(1)}` : "—"}
          unit={target?.gap_tco2e != null ? "tCO₂e" : ""}
          sub={target ? (target.on_track ? "Ahead of glide path" : `Behind ${target.name?.split(" ")[0]} target`) : "No target set"}
          color={target?.on_track ? "text-[#059669]" : target?.gap_tco2e ? "text-amber-600" : "text-slate-400"}
          icon={Target}
        />
        <MetricCard
          label="Planned Reduction"
          value={initiatives.planned_reduction_tco2e?.toFixed(1) || "0"}
          unit="tCO₂e/yr"
          sub={`${initiatives.planned || 0} planned, ${initiatives.in_progress || 0} in progress`}
          color="text-blue-600"
          icon={Zap}
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Scope breakdown */}
        <SectionCard title="Scope Breakdown" subtitle="tCO₂e by GHG scope">
          <ResponsiveContainer width="100%" height={150}>
            <BarChart data={scopeData} barSize={28}>
              <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
              <YAxis hide />
              <Tooltip contentStyle={{ background: "white", border: "1px solid #E2E8F0", borderRadius: "8px", fontSize: 12 }} formatter={v => [`${v} tCO₂e`]} />
              <Bar dataKey="value" radius={[4,4,0,0]}>
                {scopeData.map((d, i) => <Cell key={i} fill={d.fill} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </SectionCard>

        {/* Year-on-year trend */}
        <SectionCard title="Year-on-Year Trend" subtitle="tCO₂e vs target trajectory">
          {trendData.length >= 2 ? (
            <ResponsiveContainer width="100%" height={150}>
              <LineChart data={trendData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                <XAxis dataKey="year" tick={{ fontSize: 10, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: "#94A3B8" }} axisLine={false} tickLine={false} width={40} />
                <Tooltip contentStyle={{ background: "white", border: "1px solid #E2E8F0", borderRadius: "8px", fontSize: 12 }}
                  formatter={(v, name) => [`${v?.toFixed(2)} tCO₂e`, name === "total" ? "Actual" : "Target"]} />
                <Line type="monotone" dataKey="total" stroke="#0F172A" strokeWidth={2} dot={{ r: 4, fill: "#0F172A" }} name="Actual" />
                {trendData.some(d => d.target) && (
                  <Line type="monotone" dataKey="target" stroke="#059669" strokeWidth={1.5} strokeDasharray="4 4" dot={false} name="Target" />
                )}
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-36 flex items-center justify-center text-xs text-slate-400">
              Multiple years of data needed for trend view
            </div>
          )}
        </SectionCard>

        {/* Category breakdown */}
        <SectionCard title="By Emission Category" subtitle="tCO₂e">
          {catData.length > 0 ? (
            <ResponsiveContainer width="100%" height={150}>
              <BarChart data={catData} layout="vertical" barSize={12}>
                <XAxis type="number" tick={{ fontSize: 9, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 9, fill: "#374151" }} axisLine={false} tickLine={false} width={70} />
                <Tooltip contentStyle={{ background: "white", border: "1px solid #E2E8F0", borderRadius: "8px", fontSize: 12 }} formatter={v => [`${v} tCO₂e`]} />
                <Bar dataKey="value" radius={[0,4,4,0]}>
                  {catData.map((d, i) => <Cell key={i} fill={CAT_COLORS[d.name.toLowerCase()] || "#94A3B8"} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : <div className="h-36 flex items-center justify-center text-xs text-slate-400">No category data</div>}
        </SectionCard>
      </div>

      {/* Asset Heatmap */}
      <SectionCard
        title="Asset Emissions Heatmap"
        subtitle="All assets — coverage, scores, benchmark"
        action={<button onClick={() => navigate("/corporate/structure")} className="text-xs text-[#059669] hover:underline flex items-center gap-1">Manage <ChevronRight size={12} /></button>}
        noPadding
      >
        <AssetHeatmap assets={assets} totalTco2e={emissions.total_tco2e} />
      </SectionCard>

      {/* Target + Initiatives Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <SectionCard title="Target Progress" action={<button onClick={() => navigate("/corporate/targets")} className="text-xs text-[#059669] hover:underline">Manage targets</button>}>
          <TargetGauge target={target} currentTco2e={emissions.total_tco2e} />
        </SectionCard>

        <SectionCard title="Decarbonisation Initiatives" action={<button onClick={() => navigate("/corporate/initiatives")} className="text-xs text-[#059669] hover:underline">Manage</button>}>
          {initiatives.total > 0 ? (
            <div className="space-y-3">
              {[
                { label: "Planned",     count: initiatives.planned,     color: "bg-slate-300" },
                { label: "In Progress", count: initiatives.in_progress, color: "bg-blue-500" },
                { label: "Completed",   count: initiatives.completed,   color: "bg-emerald-500" },
              ].map(({ label, count, color }) => (
                <div key={label}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-slate-600">{label}</span>
                    <span className="font-medium">{count} initiative{count !== 1 ? "s" : ""}</span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className={`h-full ${color} rounded-full`} style={{ width: `${(count / initiatives.total) * 100}%` }} />
                  </div>
                </div>
              ))}
              <div className="pt-2 border-t border-slate-100 grid grid-cols-2 gap-4 text-xs">
                <div className="p-2 bg-blue-50 rounded-lg">
                  <p className="text-slate-400 mb-0.5">Planned reduction</p>
                  <p className="font-bold text-blue-700">{initiatives.planned_reduction_tco2e?.toFixed(1)} tCO₂e/yr</p>
                </div>
                <div className="p-2 bg-emerald-50 rounded-lg">
                  <p className="text-slate-400 mb-0.5">Achieved</p>
                  <p className="font-bold text-emerald-700">{initiatives.achieved_reduction_tco2e?.toFixed(1)} tCO₂e/yr</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-4">
              <Zap size={24} className="mx-auto mb-2 text-slate-200" />
              <p className="text-xs text-slate-400">No initiatives yet</p>
              <button onClick={() => navigate("/corporate/initiatives")} className="mt-2 text-xs text-[#059669] hover:underline">Add initiative</button>
            </div>
          )}
        </SectionCard>
      </div>

      {/* Climate Stress Test */}
      <SectionCard
        title="Corporate Climate Stress Test"
        action={<button onClick={() => setShowStress(!showStress)} className="text-xs text-slate-400 hover:text-slate-600">{showStress ? "Hide" : "Show scenarios"}</button>}
      >
        {showStress && assets.filter(a => a.assessment_id).length > 0 ? (
          <StressTest assessmentId={parseInt(assets.find(a => a.assessment_id)?.assessment_id)} embedded />
        ) : showStress ? (
          <p className="text-xs text-slate-400 py-2">No measured assets with assessments.</p>
        ) : null}
      </SectionCard>
      </>
      )}
    </div>
  );
}
